import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { formatDate } from "@/lib/format";
import { Search } from "lucide-react";

const ROLES = ["Admin", "Manager", "Team Leader", "Telecaller", "Coordinator", "Connector", "Client"];

export default function Team() {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const { data: team = [], isLoading } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });

  const filtered = useMemo(() => {
    let list = team;
    if (role !== "all") list = list.filter((u) => u.role === role);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((u) => [u.name, u.email, u.phone, u.employee_id].some((v) => String(v || "").toLowerCase().includes(q)));
    return list;
  }, [team, search, role]);

  return (
    <div data-testid="team-page">
      <PageHeader title="Team" subtitle={`${filtered.length} members`} />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="team-search-input" placeholder="Search name, email, ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-full sm:w-48 bg-white dark:bg-slate-950" data-testid="team-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All roles</SelectItem>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="team-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Employee ID</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Role</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Email</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 8 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No members found.</TableCell></TableRow>}
            {!isLoading && filtered.map((u) => (
              <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                <TableCell className="font-num text-xs text-slate-500">{u.employee_id || "—"}</TableCell>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{u.name}</TableCell>
                <TableCell><Badge variant="outline" className="border-slate-200 dark:border-slate-700">{u.role}</Badge></TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{u.email}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{u.phone || "—"}</TableCell>
                <TableCell className="text-slate-500 text-sm">{formatDate(u.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
